import React from "react";

/**
 * SEO — Renders <title>, <meta>, and <link rel="canonical"> tags.
 * React 19 hoists these into <head> automatically.
 *
 * Props:
 *  - title: page title (gets wrapped with site name)
 *  - description: meta description
 *  - image: full URL to a representative image (for OpenGraph / Twitter cards)
 *  - type: og:type (default "website")
 *  - path: canonical path (e.g. "/shop/derrick-deck")
 *  - noindex: when true, adds robots noindex
 */
export const SEO = ({
  title,
  description,
  image,
  type = "website",
  path,
  noindex = false,
}) => {
  const siteName = "Chibah Skateboards";
  const fullTitle = title ? `${title} · ${siteName}` : `${siteName} · Made Under Suspicious Circumstances`;
  const desc =
    description ||
    "Chibah Skateboards — stories, evidence, and bad ideas from the dog house. Decks, apparel, and accessories made under suspicious circumstances.";
  const origin =
    (typeof window !== "undefined" && window.location ? window.location.origin : "") || "";
  const canonical = path ? `${origin}${path}` : origin || undefined;
  const ogImage = image || `${origin}/icons/skull-e.png`;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {/* OpenGraph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content={siteName} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
};

export default SEO;
